import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, CalendarX, Loader2 } from "lucide-react";
import { API } from "@/config";

const fmtDate = (d) =>
  new Date(d + "T12:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

export default function Cancel() {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/appointments/cancel/${token}`)
      .then((res) => setAppt(res.data))
      .catch((err) => setError(err.response?.data?.detail || "No se encontró la cita"));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/appointments/cancel/${token}`);
      setAppt(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="cancel-page" className="min-h-screen bg-cream text-obsidian flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md rounded-3xl bg-creamcard border border-sand p-8 sm:p-10 text-center">
        <Link to="/" className="font-display text-2xl text-obsidian">
          ISI<span className="text-gold">'S</span> Nails
        </Link>

        {error && (
          <p data-testid="cancel-error" className="mt-8 text-sm text-red-700">{error}</p>
        )}

        {!error && !appt && <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto mt-10" />}

        {appt && appt.status === "cancelada" && (
          <div className="mt-8" data-testid="cancel-done">
            <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-4">Cita cancelada</h1>
            <p className="text-sm text-taupe mt-3 leading-relaxed">
              Hemos liberado tu hora del {fmtDate(appt.date)} a las {appt.time}. Cuando quieras volver, estaremos encantadas de recibirte.
            </p>
            <Link
              to="/"
              data-testid="cancel-back-home"
              className="inline-block mt-6 rounded-full bg-gold hover:bg-gold-dark text-white text-sm font-medium px-8 py-3 transition-colors"
            >
              Reservar otra cita
            </Link>
          </div>
        )}

        {appt && appt.status !== "cancelada" && (
          <div className="mt-8" data-testid="cancel-confirm">
            <CalendarX className="w-12 h-12 text-gold mx-auto" />
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-4">¿Cancelar tu cita?</h1>
            <p className="text-sm text-taupe mt-3 leading-relaxed">
              Hola {appt.name}, tu cita de <b>{appt.service}</b> es el <span className="capitalize">{fmtDate(appt.date)}</span> a las <b>{appt.time}</b>.
            </p>
            <button
              onClick={cancel}
              disabled={busy}
              data-testid="cancel-confirm-btn"
              className="mt-6 w-full rounded-xl bg-obsidian hover:bg-cocoa disabled:opacity-50 text-white text-sm font-medium py-3.5 transition-colors flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Sí, cancelar mi cita
            </button>
            <Link to="/" data-testid="cancel-keep-link" className="inline-block mt-4 text-sm text-taupe hover:text-gold transition-colors">
              No, mantener mi cita
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
